import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Container, Row, Col, Spinner } from "reactstrap";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bar, Line, Pie, Doughnut } from "react-chartjs-2";
import {
  API_URL_chatbot_access,
  API_URL_chatbot_chat,
  API_URL_chatbot_conversations,
} from "../constants";
import { getAuthHeaders } from "../utils/auth";

// Color palette for chart datasets (LSSI red + complementary).
const CHART_PALETTE = [
  "#dc3545", "#0ea5e9", "#10b981", "#f59e0b",
  "#8b5cf6", "#ec4899", "#14b8a6", "#6b7280",
];

const CHART_COMPONENTS = { bar: Bar, line: Line, pie: Pie, doughnut: Doughnut };

// ----- CSV utilities -----

const csvEscape = (s) => {
  const text = String(s ?? "");
  // Quote if it contains , " \n \r ; or starts/ends with space
  if (/[",\n\r;]|^\s|\s$/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const tableElementToCsv = (tableEl) => {
  const rows = Array.from(tableEl.querySelectorAll("tr"));
  return rows
    .map((tr) =>
      Array.from(tr.querySelectorAll("th, td"))
        .map((cell) => csvEscape((cell.innerText || "").trim()))
        .join(",")
    )
    .join("\n");
};

const downloadCsv = (csvText, filename = "datos.csv") => {
  // Prepend UTF-8 BOM so Excel/Numbers open it with the right encoding (handles ñ, á, etc.)
  const blob = new Blob(["﻿" + csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ----- Markdown table with built-in CSV export -----

const MarkdownTable = ({ node, ...props }) => {
  const tableRef = React.useRef(null);
  const handleDownload = () => {
    if (!tableRef.current) return;
    const ts = new Date().toISOString().slice(0, 10);
    downloadCsv(tableElementToCsv(tableRef.current), `lssi_${ts}.csv`);
  };
  return (
    <div style={{ position: "relative", margin: "10px 0" }}>
      <button
        type="button"
        onClick={handleDownload}
        title="Descargar como CSV"
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          padding: "3px 9px",
          fontSize: "0.75rem",
          fontWeight: 600,
          backgroundColor: "#fff",
          color: "#dc3545",
          border: "1px solid #dc3545",
          borderRadius: 6,
          cursor: "pointer",
          zIndex: 1,
        }}
      >
        ⬇ CSV
      </button>
      <div style={{ overflowX: "auto" }}>
        <table
          ref={tableRef}
          {...props}
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.875rem",
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            overflow: "hidden",
          }}
        />
      </div>
    </div>
  );
};

// ----- Standalone CSV download card (for ```csv fenced blocks) -----

const CsvDownloadCard = ({ content }) => {
  // Optional first line: "filename: my_file.csv" — model can hint at a name.
  let filename = `lssi_${new Date().toISOString().slice(0, 10)}.csv`;
  let body = content;
  const firstNl = content.indexOf("\n");
  if (firstNl > 0) {
    const firstLine = content.slice(0, firstNl).trim();
    const m = /^#?\s*filename:\s*(.+?\.csv)$/i.exec(firstLine);
    if (m) {
      filename = m[1].replace(/[^a-zA-Z0-9_.-]/g, "_");
      body = content.slice(firstNl + 1);
    }
  }
  const rowCount = body.split(/\r?\n/).filter((l) => l.trim()).length;
  return (
    <div
      style={{
        margin: "12px 0",
        padding: 14,
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        backgroundColor: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <span style={{ fontSize: "1.5rem" }} aria-hidden>📄</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "0.9375rem", color: "#1f2937" }}>{filename}</div>
        <div style={{ fontSize: "0.8125rem", color: "#6b7280" }}>{rowCount} filas (incluye encabezado)</div>
      </div>
      <button
        type="button"
        onClick={() => downloadCsv(body, filename)}
        style={{
          padding: "8px 14px",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "#fff",
          backgroundColor: "#dc3545",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        Descargar
      </button>
    </div>
  );
};

const ChartRenderer = ({ spec }) => {
  try {
    const type = (spec.type || "bar").toLowerCase();
    const Component = CHART_COMPONENTS[type] || Bar;

    const datasets = (spec.datasets || []).map((ds, i) => {
      const color = ds.backgroundColor || CHART_PALETTE[i % CHART_PALETTE.length];
      const isFill = type === "pie" || type === "doughnut";
      return {
        label: ds.label || `Serie ${i + 1}`,
        data: ds.data || [],
        backgroundColor: isFill
          ? (ds.data || []).map((_, j) => CHART_PALETTE[j % CHART_PALETTE.length])
          : color + "cc",
        borderColor: color,
        borderWidth: 1.5,
        tension: type === "line" ? 0.25 : undefined,
      };
    });

    const data = { labels: spec.labels || [], datasets };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: spec.title
          ? { display: true, text: spec.title, font: { size: 14, weight: 600 } }
          : { display: false },
        legend: { position: "top", labels: { boxWidth: 12, font: { size: 12 } } },
      },
      scales:
        type === "pie" || type === "doughnut"
          ? {}
          : { y: { beginAtZero: true } },
      ...(spec.options || {}),
    };

    return (
      <div
        style={{
          margin: "12px 0",
          padding: 16,
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          backgroundColor: "#fff",
          height: 360,
        }}
      >
        <Component data={data} options={options} />
      </div>
    );
  } catch (e) {
    return (
      <div style={{ padding: 12, color: "#dc3545", fontSize: "0.85em" }}>
        Error rendering chart: {e.message}
      </div>
    );
  }
};

const markdownComponents = {
  table: MarkdownTable,
  thead: (props) => <thead {...props} style={{ backgroundColor: "#f3f4f6" }} />,
  th: ({ node, ...props }) => (
    <th
      {...props}
      style={{
        textAlign: "left",
        padding: "10px 12px",
        borderBottom: "2px solid #e5e7eb",
        fontWeight: 600,
        color: "#374151",
      }}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      {...props}
      style={{
        padding: "8px 12px",
        borderBottom: "1px solid #f3f4f6",
        color: "#1f2937",
      }}
    />
  ),
  tr: ({ node, ...props }) => <tr {...props} />,
  h1: (props) => <h2 {...props} style={{ fontSize: "1.25rem", margin: "12px 0 8px", fontWeight: 700 }} />,
  h2: (props) => <h3 {...props} style={{ fontSize: "1.1rem", margin: "12px 0 6px", fontWeight: 700 }} />,
  h3: (props) => <h4 {...props} style={{ fontSize: "1rem", margin: "10px 0 4px", fontWeight: 600 }} />,
  ul: (props) => <ul {...props} style={{ paddingLeft: 22, margin: "6px 0" }} />,
  ol: (props) => <ol {...props} style={{ paddingLeft: 22, margin: "6px 0" }} />,
  li: (props) => <li {...props} style={{ margin: "2px 0" }} />,
  p: (props) => <p {...props} style={{ margin: "6px 0", lineHeight: 1.5 }} />,
  code: ({ inline, className, children, ...props }) => {
    // Detect ```chart fenced blocks → render a Chart.js chart instead of code.
    if (!inline && /\blanguage-chart\b/.test(className || "")) {
      const raw = String(children).replace(/\n$/, "");
      try {
        const spec = JSON.parse(raw);
        return <ChartRenderer spec={spec} />;
      } catch (e) {
        // JSON not complete yet (still streaming) or invalid — fall through to
        // code rendering so the user sees the in-progress payload.
      }
    }
    // Detect ```csv fenced blocks → render a download card.
    if (!inline && /\blanguage-csv\b/.test(className || "")) {
      const raw = String(children).replace(/\n$/, "");
      // Only render the card once we have at least 2 lines (header + 1 row).
      // While streaming, fall through to code rendering until it's complete.
      const lineCount = raw.split(/\r?\n/).filter((l) => l.trim()).length;
      if (lineCount >= 2) {
        return <CsvDownloadCard content={raw} />;
      }
    }
    return inline ? (
      <code
        {...props}
        className={className}
        style={{
          backgroundColor: "#f3f4f6",
          padding: "1px 6px",
          borderRadius: 4,
          fontSize: "0.85em",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {children}
      </code>
    ) : (
      <pre style={{
        backgroundColor: "#1f2937",
        color: "#f9fafb",
        padding: 12,
        borderRadius: 8,
        overflowX: "auto",
        fontSize: "0.85em",
      }}>
        <code className={className} {...props}>{children}</code>
      </pre>
    );
  },
  blockquote: (props) => (
    <blockquote
      {...props}
      style={{
        borderLeft: "3px solid #dc3545",
        paddingLeft: 12,
        margin: "8px 0",
        color: "#4b5563",
        fontStyle: "italic",
      }}
    />
  ),
  hr: () => <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "12px 0" }} />,
  a: (props) => <a {...props} style={{ color: "#dc3545", textDecoration: "underline" }} target="_blank" rel="noopener noreferrer" />,
};

const ThinkingIndicator = ({ toolActivity, elapsedSec }) => {
  const label = toolActivity || "Pensando";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        fontStyle: "italic",
        fontSize: "0.9375rem",
        padding: "2px 0",
      }}
    >
      <span aria-hidden style={{ fontSize: "1rem" }}>
        {toolActivity ? "🔎" : "💭"}
      </span>
      <span>{label}</span>
      <span className="lssi-thinking-dots" aria-hidden>
        <span>.</span>
        <span>.</span>
        <span>.</span>
      </span>
      {elapsedSec >= 8 && (
        <span style={{ marginLeft: 6, fontSize: "0.8125rem", color: "#9ca3af" }}>
          ({elapsedSec}s)
        </span>
      )}
      {elapsedSec >= 15 && (
        <span style={{ marginLeft: 4, fontSize: "0.8125rem", color: "#9ca3af" }}>
          — análisis complejo, dame un momento más
        </span>
      )}
    </div>
  );
};

const AI = () => {
  const [accessChecked, setAccessChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [toolActivity, setToolActivity] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const [conversations, setConversations] = useState([]);
  const [convsLoading, setConvsLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get("c") ? Number(searchParams.get("c")) : null;

  const transcriptRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const streamStartRef = useRef(null);

  useEffect(() => {
    const headers = getAuthHeaders();
    axios
      .get(API_URL_chatbot_access, { headers })
      .then((res) => setAllowed(!!res.data?.allowed))
      .catch(() => setAllowed(false))
      .finally(() => setAccessChecked(true));
  }, []);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages, toolActivity]);

  const loadConversations = useCallback(() => {
    if (!allowed) return;
    setConvsLoading(true);
    axios
      .get(API_URL_chatbot_conversations, { headers: getAuthHeaders() })
      .then((res) => setConversations(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setConvsLoading(false));
  }, [allowed]);

  // Fetch the sidebar list once we know the user is allowed.
  useEffect(() => {
    if (allowed) loadConversations();
  }, [allowed, loadConversations]);

  // When ?c=<id> is in the URL, load that conversation's history.
  useEffect(() => {
    if (!allowed || !conversationId) {
      if (!conversationId) setMessages([]);
      return;
    }
    axios
      .get(`${API_URL_chatbot_conversations}${conversationId}/`, { headers: getAuthHeaders() })
      .then((res) => {
        const msgs = (res.data?.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        }));
        setMessages(msgs);
      })
      .catch(() => {
        // Conversation missing or unauthorized — drop back to a new chat.
        setSearchParams({}, { replace: true });
        setMessages([]);
      });
  }, [conversationId, allowed, setSearchParams]);

  const startNewConversation = () => {
    setMessages([]);
    setSearchParams({}, { replace: true });
  };

  const selectConversation = (id) => {
    setSearchParams({ c: String(id) });
  };

  const deleteConversation = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("¿Borrar esta conversación?")) return;
    try {
      await axios.delete(`${API_URL_chatbot_conversations}${id}/`, { headers: getAuthHeaders() });
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (conversationId === id) startNewConversation();
    } catch (err) {
      console.error("delete conversation:", err);
    }
  };

  if (!accessChecked) {
    return (
      <Container className="text-center my-5">
        <Spinner color="primary" />
      </Container>
    );
  }
  if (!allowed) return <Navigate to="/home" replace />;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    setMessages((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "" },
    ]);
    setInput("");
    setStreaming(true);
    setToolActivity(null);
    setElapsedSec(0);
    streamStartRef.current = Date.now();
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    elapsedTimerRef.current = setInterval(() => {
      if (streamStartRef.current) {
        setElapsedSec(Math.floor((Date.now() - streamStartRef.current) / 1000));
      }
    }, 1000);

    try {
      const headers = {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      };
      const resp = await fetch(API_URL_chatbot_chat, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ message: text, conversation_id: conversationId }),
      });

      if (!resp.ok) {
        const err = await resp.text();
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: `Error: ${err}` };
          return next;
        });
        setStreaming(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }

          if (evt.type === "text") {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              next[next.length - 1] = {
                ...last,
                content: (last.content || "") + (evt.content || ""),
              };
              return next;
            });
          } else if (evt.type === "tool_start") {
            setToolActivity(`Consultando: ${evt.name}…`);
          } else if (evt.type === "tool_end") {
            setToolActivity(null);
          } else if (evt.type === "done") {
            if (evt.conversation_id) {
              // If this was a fresh chat (no id in URL yet), put it there now.
              if (!conversationId) {
                setSearchParams({ c: String(evt.conversation_id) }, { replace: true });
              }
              // Refresh the sidebar so the new/updated conversation appears at the top.
              loadConversations();
            }
          } else if (evt.type === "error") {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              const errText = `\n\n_Error: ${evt.message}_`;
              next[next.length - 1] = {
                ...last,
                content: (last.content || "") + errText,
              };
              return next;
            });
          }
        }
      }
    } catch (err) {
      console.error("chat stream error:", err);
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        next[next.length - 1] = {
          ...last,
          content: (last.content || "") + `\n\n_Error de red: ${err.message}_`,
        };
        return next;
      });
    } finally {
      setStreaming(false);
      setToolActivity(null);
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
      streamStartRef.current = null;
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Container fluid style={{ marginTop: "20px", maxWidth: 1500, paddingLeft: 24, paddingRight: 24 }}>
      <Row>
        <Col>
          <h1 style={{ marginBottom: 4 }}>Asistente AI</h1>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            Pregunta sobre márgenes, proyectos, value streams, partners. Todas las respuestas se basan en datos del sistema.
          </p>
        </Col>
      </Row>

      <div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
        {/* Sidebar: conversation history */}
        <aside
          style={{
            width: 280,
            flexShrink: 0,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(60vh + 80px)",
          }}
        >
          <div style={{ padding: 12, borderBottom: "1px solid #f3f4f6" }}>
            <button
              onClick={startNewConversation}
              disabled={streaming}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: streaming ? "#9ca3af" : "#dc3545",
                border: "none",
                borderRadius: 8,
                cursor: streaming ? "not-allowed" : "pointer",
              }}
            >
              + Nueva conversación
            </button>
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {convsLoading ? (
              <div style={{ padding: 16, textAlign: "center" }}>
                <Spinner size="sm" color="secondary" />
              </div>
            ) : conversations.length === 0 ? (
              <div style={{ padding: 14, color: "#9ca3af", fontSize: "0.8125rem", fontStyle: "italic" }}>
                Sin conversaciones previas.
              </div>
            ) : (
              conversations.map((c) => {
                const active = c.id === conversationId;
                return (
                  <div
                    key={c.id}
                    onClick={() => !streaming && selectConversation(c.id)}
                    style={{
                      padding: "10px 12px",
                      borderBottom: "1px solid #f3f4f6",
                      cursor: streaming ? "not-allowed" : "pointer",
                      backgroundColor: active ? "#fef2f2" : "transparent",
                      borderLeft: active ? "3px solid #dc3545" : "3px solid transparent",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 6,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: active ? 600 : 500,
                          color: "#1f2937",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={c.title || "(sin título)"}
                      >
                        {c.title || "(sin título)"}
                      </div>
                      <div style={{ fontSize: "0.6875rem", color: "#9ca3af", marginTop: 2 }}>
                        {new Date(c.updated_at).toLocaleString("es", {
                          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteConversation(c.id, e)}
                      disabled={streaming}
                      title="Borrar"
                      style={{
                        background: "none",
                        border: "none",
                        color: "#9ca3af",
                        cursor: streaming ? "not-allowed" : "pointer",
                        padding: 2,
                        fontSize: "1rem",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Main: transcript + input */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div
            ref={transcriptRef}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 16,
          minHeight: 360,
          maxHeight: "60vh",
          overflowY: "auto",
          background: "#fafafa",
          marginBottom: 12,
        }}
      >
        {messages.length === 0 ? (
          <div className="text-muted" style={{ fontStyle: "italic" }}>
            Ejemplo: <em>"¿Cuál es el margen por sede en 2025?"</em> · <em>"¿Por qué la sede Norte tiene menor margen que Sur?"</em> · <em>"Top 5 partners por utilidad."</em>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: m.role === "user" ? "#1f2937" : "#dc3545",
                marginBottom: 4,
              }}>
                {m.role === "user" ? "Tú" : "Asistente"}
              </div>
              {m.role === "assistant" ? (
                <div style={{ lineHeight: 1.5 }}>
                  {m.content ? (
                    <>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {m.content}
                      </ReactMarkdown>
                      {/* Mid-response tool call: show inline beneath the text */}
                      {streaming && i === messages.length - 1 && toolActivity && (
                        <ThinkingIndicator toolActivity={toolActivity} elapsedSec={elapsedSec} />
                      )}
                    </>
                  ) : (
                    streaming && i === messages.length - 1 ? (
                      <ThinkingIndicator toolActivity={toolActivity} elapsedSec={elapsedSec} />
                    ) : ""
                  )}
                </div>
              ) : (
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {m.content}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <style>{`
        .lssi-thinking-dots { display: inline-flex; gap: 1px; }
        .lssi-thinking-dots span {
          animation: lssi-blink 1.4s infinite both;
          font-weight: 600;
        }
        .lssi-thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
        .lssi-thinking-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes lssi-blink {
          0%, 80%, 100% { opacity: 0.2; }
          40% { opacity: 1; }
        }
      `}</style>

      <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8 }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Haz una pregunta..."
          disabled={streaming}
          rows={2}
          style={{
            flex: 1,
            padding: "10px 14px",
            fontSize: "0.9375rem",
            border: "1.5px solid #e5e7eb",
            borderRadius: 10,
            backgroundColor: "#fff",
            resize: "vertical",
          }}
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          style={{
            padding: "10px 18px",
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "#fff",
            backgroundColor: streaming || !input.trim() ? "#9ca3af" : "#dc3545",
            border: "none",
            borderRadius: 10,
            cursor: streaming || !input.trim() ? "not-allowed" : "pointer",
          }}
        >
          {streaming ? "..." : "Enviar"}
        </button>
      </form>
        </div> {/* end main column */}
      </div> {/* end sidebar+main flex row */}
    </Container>
  );
};

export default AI;
